import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, ChevronDown, ChevronUp, X, Plus } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { Button, ThemedText } from '../components';
import { FormGroup, FormLabel } from '../components/FormStyles';
import { StatusBadge } from '../components/Badge';
import labDocumentFormatService from '../api/services/labDocumentFormatService';

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 28px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.primary || props.theme.colors.tint};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: ${props => props.theme.borderRadius.md};
  opacity: 0.8;
  transition: all 0.2s;
  margin-left: -${props => props.theme.spacing.sm};
  &:hover { opacity: 1; background-color: ${props => props.theme.colors.primary}10; }
`;

const Card = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
`;

const StyledInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  width: 100%;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const MonoInput = styled(StyledInput)`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
`;

const StyledTextarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  width: 100%;
  min-height: 80px;
  resize: vertical;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const MonoTextarea = styled(StyledTextarea)`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
  min-height: 120px;
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const SliderInput = styled.input.attrs({ type: 'range' })`
  flex: 1;
  accent-color: ${props => props.theme.colors.tint};
`;

const SliderValue = styled.span`
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  min-width: 36px;
  text-align: right;
`;

const NumberInput = styled(StyledInput).attrs({ type: 'number' })`
  width: 120px;
`;

const CollapsibleHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.$isOpen ? props.theme.spacing.md : '0'};
  padding-bottom: ${props => props.$isOpen ? props.theme.spacing.sm : '0'};
  border-bottom: ${props => props.$isOpen ? `1px solid ${props.theme.colors.border}` : 'none'};
  text-align: left;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: ${props => props.theme.spacing.sm};
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background-color: ${props => props.theme.colors.tint}15;
  color: ${props => props.theme.colors.tint};
  border: 1px solid ${props => props.theme.colors.tint}30;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
`;

const TagRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  padding: 0;
  opacity: 0.6;
  &:hover { opacity: 1; }
`;

const TagInputRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const StatusBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatusLabel = styled.span`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
`;

const StatusValue = styled.span`
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
`;

const PreBlock = styled.pre`
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  color: ${props => props.theme.colors.text};
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  justify-content: flex-end;
`;

const Spinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xxl};
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid ${props => props.theme.colors.border};
    border-top-color: ${props => props.theme.colors.tint};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

export const LabFormatDetailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [format, setFormat] = useState(null);
  const [form, setForm] = useState({
    name: '',
    formatCode: '',
    labVendor: '',
    description: '',
    detectionPriority: 0,
    detectionConfidenceThreshold: 0.7,
    isDefault: false,
    detectionKeywords: [],
    sampleReferencePattern: '',
    userHintsText: '',
    classifyPromptJson: '',
    structurePromptJson: '',
    valuePromptJson: '',
    calibrationNotes: '',
  });
  const [newKeyword, setNewKeyword] = useState('');
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [exampleDataOpen, setExampleDataOpen] = useState(false);

  const loadFormat = useCallback(async () => {
    setLoading(true);
    try {
      const res = await labDocumentFormatService.getById(id);
      const f = res?.data || res;
      setFormat(f);
      setForm({
        name: f.name || '',
        formatCode: f.formatCode || '',
        labVendor: f.labVendor || '',
        description: f.description || '',
        detectionPriority: f.detectionPriority ?? 0,
        detectionConfidenceThreshold: f.detectionConfidenceThreshold ?? 0.7,
        isDefault: f.isDefault || false,
        detectionKeywords: f.detectionKeywords || [],
        sampleReferencePattern: f.sampleReferencePattern || '',
        userHintsText: f.userHintsText || '',
        classifyPromptJson: f.classifyPromptJson || '',
        structurePromptJson: f.structurePromptJson || '',
        valuePromptJson: f.valuePromptJson || '',
        calibrationNotes: f.calibrationNotes || '',
      });
    } catch {
      toast.error('Failed to load format');
      navigate('/lab-formats');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadFormat(); }, [loadFormat]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addKeyword = () => {
    const kw = newKeyword.trim();
    if (!kw) return;
    if (form.detectionKeywords.includes(kw)) {
      toast.warning('Keyword already exists');
      return;
    }
    setForm(prev => ({ ...prev, detectionKeywords: [...prev.detectionKeywords, kw] }));
    setNewKeyword('');
  };

  const removeKeyword = (kw) => {
    setForm(prev => ({
      ...prev,
      detectionKeywords: prev.detectionKeywords.filter(k => k !== kw),
    }));
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.formatCode.trim()) {
      toast.error('Format Code is required');
      return;
    }

    setSaving(true);
    try {
      await labDocumentFormatService.update(id, form);
      toast.success('Format updated');
      loadFormat();
    } catch {
      toast.error('Failed to save format');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageContainer theme={theme}>
          <Spinner theme={theme} />
        </PageContainer>
      </Layout>
    );
  }

  if (!format) return null;

  return (
    <Layout>
      <PageContainer theme={theme}>
        {/* Header */}
        <HeaderSection theme={theme}>
          <HeaderTopRow>
            <BackButton theme={theme} onClick={() => navigate('/lab-formats')}>
              <ArrowLeft size={14} /> Lab Formats
            </BackButton>
            <ButtonRow theme={theme}>
              <Button onClick={handleSave} disabled={saving}>
                <Save size={14} style={{ marginRight: 6 }} />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ButtonRow>
          </HeaderTopRow>
          <ThemedText $size="h2" $weight="bold">{format.name}</ThemedText>
        </HeaderSection>

        {/* Section 1: Basic Info */}
        <Card theme={theme}>
          <CardTitle theme={theme}>Basic Info</CardTitle>
          <FormGrid theme={theme}>
            <FormGroup theme={theme}>
              <FormLabel theme={theme}>Name *</FormLabel>
              <StyledInput theme={theme} value={form.name}
                onChange={e => updateField('name', e.target.value)}
                placeholder="e.g. Element Materials Standard Report" />
            </FormGroup>
            <FormGroup theme={theme}>
              <FormLabel theme={theme}>Format Code *</FormLabel>
              <StyledInput theme={theme} value={form.formatCode}
                onChange={e => updateField('formatCode', e.target.value)}
                placeholder="e.g. ELEMENT-STANDARD-V2" />
            </FormGroup>
            <FormGroup theme={theme}>
              <FormLabel theme={theme}>Lab Vendor</FormLabel>
              <StyledInput theme={theme} value={form.labVendor}
                onChange={e => updateField('labVendor', e.target.value)}
                placeholder="e.g. Element, ALS, SGS" />
            </FormGroup>
            <FormGroup theme={theme}>
              <FormLabel theme={theme}>Detection Priority</FormLabel>
              <NumberInput theme={theme} value={form.detectionPriority}
                onChange={e => updateField('detectionPriority', parseInt(e.target.value) || 0)}
                min={0} />
            </FormGroup>
          </FormGrid>
          <FormGroup theme={theme} style={{ marginTop: theme.spacing.md }}>
            <FormLabel theme={theme}>Description</FormLabel>
            <StyledTextarea theme={theme} value={form.description}
              onChange={e => updateField('description', e.target.value)}
              placeholder="Describe the document format and layout" />
          </FormGroup>
          <FormGroup theme={theme}>
            <FormLabel theme={theme}>Confidence Threshold</FormLabel>
            <SliderContainer theme={theme}>
              <SliderInput theme={theme}
                min={0} max={1} step={0.05}
                value={form.detectionConfidenceThreshold}
                onChange={e => updateField('detectionConfidenceThreshold', parseFloat(e.target.value))} />
              <SliderValue theme={theme}>{(form.detectionConfidenceThreshold * 100).toFixed(0)}%</SliderValue>
            </SliderContainer>
          </FormGroup>
          <FormGroup theme={theme} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.isDefault}
              onChange={e => updateField('isDefault', e.target.checked)} />
            <FormLabel theme={theme} style={{ marginBottom: 0 }}>Set as default format</FormLabel>
          </FormGroup>
        </Card>

        {/* Section 2: Detection Config */}
        <Card theme={theme}>
          <CardTitle theme={theme}>Detection Config</CardTitle>
          <FormGroup theme={theme}>
            <FormLabel theme={theme}>Detection Keywords</FormLabel>
            <TagInputRow theme={theme}>
              <StyledInput theme={theme} value={newKeyword}
                onChange={e => setNewKeyword(e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                placeholder="Add a keyword and press Enter"
                style={{ flex: 1 }} />
              <Button $variant="secondary" $size="small" onClick={addKeyword} disabled={!newKeyword.trim()}>
                <Plus size={14} style={{ marginRight: 4 }} /> Add
              </Button>
            </TagInputRow>
            {form.detectionKeywords.length > 0 && (
              <TagContainer theme={theme}>
                {form.detectionKeywords.map(kw => (
                  <Tag key={kw} theme={theme}>
                    {kw}
                    <TagRemove onClick={() => removeKeyword(kw)}><X size={12} /></TagRemove>
                  </Tag>
                ))}
              </TagContainer>
            )}
          </FormGroup>
          <FormGroup theme={theme}>
            <FormLabel theme={theme}>Sample Reference Pattern</FormLabel>
            <MonoInput theme={theme} value={form.sampleReferencePattern}
              onChange={e => updateField('sampleReferencePattern', e.target.value)}
              placeholder="e.g. ^[A-Z]{2}-\d{6}$" />
          </FormGroup>
          <FormGroup theme={theme}>
            <FormLabel theme={theme}>User Hints</FormLabel>
            <StyledTextarea theme={theme} value={form.userHintsText}
              onChange={e => updateField('userHintsText', e.target.value)}
              placeholder="Hints to guide AI extraction for this format" />
          </FormGroup>
        </Card>

        {/* Section 3: AI Prompt Overrides (collapsible) */}
        <Card theme={theme}>
          <CollapsibleHeader theme={theme} $isOpen={promptsOpen} onClick={() => setPromptsOpen(!promptsOpen)}>
            {promptsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            AI Prompt Overrides
          </CollapsibleHeader>
          {promptsOpen && (
            <>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Classify Prompt (JSON)</FormLabel>
                <MonoTextarea theme={theme} value={form.classifyPromptJson}
                  onChange={e => updateField('classifyPromptJson', e.target.value)}
                  placeholder='{"system": "...", "user": "..."}' />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Structure Prompt (JSON)</FormLabel>
                <MonoTextarea theme={theme} value={form.structurePromptJson}
                  onChange={e => updateField('structurePromptJson', e.target.value)}
                  placeholder='{"system": "...", "user": "..."}' />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Value Prompt (JSON)</FormLabel>
                <MonoTextarea theme={theme} value={form.valuePromptJson}
                  onChange={e => updateField('valuePromptJson', e.target.value)}
                  placeholder='{"system": "...", "user": "..."}' />
              </FormGroup>
            </>
          )}
        </Card>

        {/* Section 4: Calibration Data */}
        <Card theme={theme}>
          <CardTitle theme={theme}>Calibration Data</CardTitle>
          <FormGroup theme={theme}>
            <FormLabel theme={theme}>Calibration Notes</FormLabel>
            <StyledTextarea theme={theme} value={form.calibrationNotes}
              onChange={e => updateField('calibrationNotes', e.target.value)}
              placeholder="Notes from calibration runs" />
          </FormGroup>
          {format.exampleExtractionJson && (
            <FormGroup theme={theme}>
              <CollapsibleHeader theme={theme} $isOpen={exampleDataOpen} onClick={() => setExampleDataOpen(!exampleDataOpen)}>
                {exampleDataOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                Example Extraction Data
              </CollapsibleHeader>
              {exampleDataOpen && (
                <PreBlock theme={theme}>
                  {(() => {
                    try { return JSON.stringify(JSON.parse(format.exampleExtractionJson), null, 2); }
                    catch { return format.exampleExtractionJson; }
                  })()}
                </PreBlock>
              )}
            </FormGroup>
          )}
          {format.exampleDocumentS3Key && (
            <FormGroup theme={theme}>
              <FormLabel theme={theme}>Example Document</FormLabel>
              <MonoInput theme={theme} value={format.exampleDocumentS3Key} readOnly
                style={{ opacity: 0.7 }} />
            </FormGroup>
          )}
        </Card>

        {/* Status bar */}
        <Card theme={theme}>
          <CardTitle theme={theme}>Status</CardTitle>
          <StatusBar theme={theme}>
            <StatusItem>
              <StatusLabel theme={theme}>Detection Count</StatusLabel>
              <StatusValue theme={theme}>{format.detectionCount ?? 0}</StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel theme={theme}>Last Detected</StatusLabel>
              <StatusValue theme={theme}>
                {format.lastDetectedAt ? new Date(format.lastDetectedAt).toLocaleString() : 'Never'}
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel theme={theme}>Created</StatusLabel>
              <StatusValue theme={theme}>
                {format.createdAt ? new Date(format.createdAt).toLocaleString() : '-'}
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel theme={theme}>Created By</StatusLabel>
              <StatusValue theme={theme}>{format.createdByName || '-'}</StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel theme={theme}>Status</StatusLabel>
              <StatusValue theme={theme}>
                <StatusBadge isActive={format.isActive !== false} />
              </StatusValue>
            </StatusItem>
          </StatusBar>
        </Card>

        {/* Bottom save button */}
        <ButtonRow theme={theme} style={{ marginBottom: theme.spacing.lg }}>
          <Button $variant="secondary" onClick={() => navigate('/lab-formats')}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save size={14} style={{ marginRight: 6 }} />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </ButtonRow>
      </PageContainer>
    </Layout>
  );
};
